import React, { useState } from 'react';
import pb from '../../lib/pocketbase';
import HomeBooking from '../../pages/Home/components/HomeBooking';
import './BookingConfigurator.css';

const BookingConfigurator = ({
    vehicle,
    services,
    dbServices,
    pricingRules,
    priceDetails,
    customer,
    user,
    showTour,
    tourStep,
    handleVehicleChange,
    handleServiceChange,
    handleCustomerChange,
    setCurrentPage,
    setShowAuthModal,
    setAuthMode,
    isQuotePage,
    onSubmitQuote,
    agencySettings
}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [showSimulation, setShowSimulation] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [bookingLoading, setBookingLoading] = useState(false);
    const totalSteps = 5;

    // Generate upcoming dates based on agency opening hours
    React.useEffect(() => {
        const h = agencySettings?.opening_hours ? (typeof agencySettings.opening_hours === 'string' ? JSON.parse(agencySettings.opening_hours) : agencySettings.opening_hours) : null;
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        
        const dates = [];
        let curr = new Date();
        curr.setHours(0, 0, 0, 0);
        
        // Max lookahead of 60 days
        let count = 0;
        while (dates.length < 45 && count < 60) {
            curr.setDate(curr.getDate() + 1);
            count++;
            
            const dayName = dayNames[curr.getDay()];
            const isOpen = h ? h[dayName]?.open : curr.getDay() !== 0; // fallback to skipping sundays
            
            if (isOpen) {
                dates.push(new Date(curr));
            }
        }
        setAvailableDates(dates);
    }, [agencySettings]);

    const handleDateSelect = async (dateObj) => {
        const dateStr = dateObj.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
        setSelectedDate(dateStr);
        setSelectedTime(null);
        setBookingLoading(true);

        const h = agencySettings?.opening_hours ? (typeof agencySettings.opening_hours === 'string' ? JSON.parse(agencySettings.opening_hours) : agencySettings.opening_hours) : null;
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[dateObj.getDay()];
        const daySet = h ? h[dayName] : null;

        try {
            // Check existing appointments in PocketBase
            const existing = await pb.collection('appointments').getList(1, 100, { filter: `date = "${dateStr}"` });
            const booked = existing.items.map(a => a.time);

            let slots = [];
            // If Agency is in busy mode or day is closed, no slots
            if (agencySettings?.busy_mode_active || (daySet && !daySet.open)) {
                slots = [];
            } else if (daySet && daySet.start && daySet.end) {
                // Generate slots based on admin hours
                const startH = parseInt(daySet.start.split(':')[0]);
                const endH = parseInt(daySet.end.split(':')[0]);
                for (let hr = startH; hr < endH; hr++) {
                    const timeStr = `${hr.toString().padStart(2, '0')}:00`;
                    slots.push(timeStr);
                }
            } else {
                // Fallback slots
                slots = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'];
            }

            setAvailableSlots(slots.filter(s => !booked.includes(s)));
        } catch (e) {
            setAvailableSlots(['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00']);
        } finally {
            setBookingLoading(false);
        }
    };

    const nextStep = () => {
        setCurrentStep(prev => Math.min(prev + 1, totalSteps));
        const configElement = document.getElementById('booking-config');
        if (configElement) configElement.scrollIntoView({ behavior: 'smooth' });
    };
    const prevStep = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
        const configElement = document.getElementById('booking-config');
        if (configElement) configElement.scrollIntoView({ behavior: 'smooth' });
    };

    const vehicleTypes = [
        { id: 'citadine', label: 'Citadine', icon: '🚗' },
        { id: 'berline', label: 'Berline', icon: '🚘' },
        { id: 'suv', label: 'SUV', icon: '🚙' },
        { id: 'luxe', label: 'Luxe', icon: '🏎️' },
        { id: 'utilitaire', label: 'Utilitaire', icon: '🚐' },
        { id: 'moto', label: 'Moto', icon: '🏍️' },
    ];

    const categories = [
        { id: 'Clés', label: 'Reproduction de clés', icon: '🔑', desc: 'Perte ou double de vos clés' },
        { id: 'Éthanol', label: 'Passage à l’Éthanol', icon: '⛽', desc: 'Économisez sur chaque plein' },
        { id: 'Diagnostic', label: 'Diagnostic Auto', icon: '💻', desc: 'Recherche de panne moteur' },
        { id: 'Moteur', label: 'Ingénierie Moteur', icon: '⚙️', desc: 'Performance et optimisation' },
    ];

    const isCategoryActive = (catId) => {
        return dbServices.filter(s => s.category === catId).some(s => services[s.id]);
    };

    const hasAnyService = Object.keys(services).some(key => services[key] && key !== 'urgent');

    const renderStepContent = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="step-content fadeIn">
                        <h3 className="step-title">Précisez votre besoin</h3>
                        <span className="form-label-title center">Que souhaitez-vous faire ?</span>
                        <div className={`category-selection-grid ${showTour && tourStep === 1 ? 'tour-highlight' : ''}`} id="tour-step-services">
                            {categories.map(cat => {
                                const active = isCategoryActive(cat.id);
                                return (
                                    <div
                                        key={cat.id}
                                        className={`category-card ${active ? 'selected' : ''}`}
                                        onClick={() => {
                                            const categoryServices = dbServices.filter(s => s.category === cat.id);
                                            if (categoryServices.length > 0) {
                                                if (active) {
                                                    categoryServices.forEach(s => {
                                                        if (services[s.id]) {
                                                            handleServiceChange({ target: { id: s.id, checked: false } });
                                                        }
                                                    });
                                                } else {
                                                    handleServiceChange({ target: { id: categoryServices[0].id, checked: true } });
                                                }
                                            }
                                        }}
                                    >
                                        <div className="cat-icon">{cat.icon}</div>
                                        <div className="cat-info">
                                            <span className="cat-label">{cat.label}</span>
                                            <span className="cat-desc">{cat.desc}</span>
                                        </div>
                                        <div className="cat-check">{active ? '✓' : '+'}</div>
                                    </div>
                                );
                            })}
                        </div>

                        {categories.some(cat => isCategoryActive(cat.id)) && (
                            <div className="sub-services-step-1 fadeIn">
                                <span className="form-label-title center">Détails de votre besoin</span>
                                <div className="checkbox-group grid-2">
                                    {dbServices
                                        .filter(s => categories.some(cat => cat.id === s.category && isCategoryActive(cat.id)))
                                        .map(service => {
                                            let sIcon = '🔧';
                                            if (service.name.toLowerCase().includes('moto')) sIcon = '🏍️';
                                            else if (service.name.toLowerCase().includes('clé') || service.name.toLowerCase().includes('cles')) sIcon = '🔑';
                                            else if (service.name.toLowerCase().includes('éthanol') || service.name.toLowerCase().includes('ethanol')) sIcon = '⛽';
                                            else if (service.category === 'Diagnostic') sIcon = '💻';

                                            return (
                                                <label key={service.id} className={`service-card-label horizontal small ${services[service.id] ? 'selected' : ''}`}>
                                                    <input
                                                        type="checkbox"
                                                        id={service.id}
                                                        checked={!!services[service.id]}
                                                        onChange={handleServiceChange}
                                                        className="service-checkbox"
                                                    />
                                                    <div className="service-info-mini">
                                                        <span className="service-icon-mini">{sIcon}</span>
                                                        <span className="service-name">{service.name}</span>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                </div>
                            </div>
                        )}
                        <div className="step-actions center">
                            <button className={`hero-cta ${showTour && tourStep === 2 ? 'tour-highlight' : ''}`} onClick={nextStep} disabled={!vehicle.plate || !hasAnyService} id="tour-step-cta">
                                Identifier mon véhicule
                            </button>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="step-content fadeIn">
                        <h3 className="step-title">Précisez votre véhicule</h3>
                        <div className="vehicle-manual-info">
                            <div className="form-row grid-2">
                                <div className="form-item">
                                    <label className="form-label-title">Marque</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Peugeot, BMW..."
                                        className="custom-input"
                                        name="brand"
                                        value={vehicle.brand || ''}
                                        onChange={handleVehicleChange}
                                    />
                                </div>
                                <div className="form-item">
                                    <label className="form-label-title">Modèle</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: 308, Clio..."
                                        className="custom-input"
                                        name="model"
                                        value={vehicle.model || ''}
                                        onChange={handleVehicleChange}
                                    />
                                </div>
                            </div>

                            <div className="form-row grid-2">
                                <div className="form-item">
                                    <label className="form-label-title">Année</label>
                                    <input
                                        type="text"
                                        placeholder="Ex: 2018"
                                        className="custom-input"
                                        name="year"
                                        value={vehicle.year || ''}
                                        onChange={handleVehicleChange}
                                    />
                                </div>
                                <div className="form-item">
                                    <label className="form-label-title">Carburant</label>
                                    <select
                                        name="fuel"
                                        className="custom-input custom-select"
                                        value={vehicle.fuel || 'essence'}
                                        onChange={handleVehicleChange}
                                    >
                                        <option value="essence">Essence</option>
                                        <option value="diesel">Diesel</option>
                                        <option value="hybride">Hybride</option>
                                        <option value="electrique">Électrique</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <span className="form-label-title center" style={{ marginTop: '1.5rem' }}>Type de carrosserie</span>
                        <div className="vehicle-type-selection">
                            {vehicleTypes.map(type => (
                                <div
                                    key={type.id}
                                    className={`type-item ${vehicle.vehicle_type === type.id ? 'selected' : ''}`}
                                    onClick={() => handleVehicleChange({ target: { name: 'vehicle_type', value: type.id } })}
                                >
                                    <span className="type-icon-large">{type.icon}</span>
                                    <span className="type-label-small">{type.label}</span>
                                </div>
                            ))}
                        </div>
                        <div className="step-actions split">
                            <button className="secondary-button" onClick={prevStep}>Retour</button>
                            <button className="hero-cta" onClick={nextStep}>
                                Continuer vers mes coordonnées
                            </button>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="step-content fadeIn">
                        <div className="step-header-mobile-friendly">
                            <h3 className="step-title" style={{ marginBottom: '0.5rem' }}>Réserver une intervention</h3>
                            <p className="step-subtitle" style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.95rem', marginBottom: '1.5rem' }}>
                                Réservez en 1 minute, avec ou sans compte.
                            </p>
                        </div>
                        <div className="contact-form-container">
                            <div className="form-row grid-2">
                                <div className="form-item">
                                    <label className="form-label-title">Prénom</label>
                                    <input
                                        type="text"
                                        name="firstName"
                                        placeholder="Votre prénom"
                                        className="custom-input"
                                        value={customer.firstName || ''}
                                        onChange={handleCustomerChange}
                                    />
                                </div>
                                <div className="form-item">
                                    <label className="form-label-title">Nom</label>
                                    <input
                                        type="text"
                                        name="lastName"
                                        placeholder="Votre nom"
                                        className="custom-input"
                                        value={customer.lastName || ''}
                                        onChange={handleCustomerChange}
                                    />
                                </div>
                            </div>
                            <div className="form-row grid-2">
                                <div className="form-item">
                                    <label className="form-label-title">Téléphone</label>
                                    <input
                                        type="tel"
                                        name="phone"
                                        placeholder="06 00 00 00 00"
                                        className="custom-input"
                                        value={customer.phone || ''}
                                        onChange={handleCustomerChange}
                                    />
                                </div>
                                <div className="form-item">
                                    <label className="form-label-title">E-mail</label>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="votre@email.com"
                                        className="custom-input"
                                        value={customer.email || ''}
                                        onChange={handleCustomerChange}
                                    />
                                </div>
                            </div>
                            <div className="form-item">
                                <label className="form-label-title">Ville / Zone d'intervention</label>
                                <input
                                    type="text"
                                    name="city"
                                    placeholder="Toulouse, Blagnac, Muret..."
                                    className="custom-input"
                                    value={customer.city || ''}
                                    onChange={handleCustomerChange}
                                />
                            </div>

                            {!user ? (
                                <div className="mandatory-account-section fadeIn" style={{ marginTop: '1rem' }}>
                                    <div className="form-row grid-2">
                                        <div className="form-item">
                                            <label className="form-label-title">Choisissez un mot de passe</label>
                                            <input
                                                type="password"
                                                name="password"
                                                placeholder="••••••••"
                                                className="custom-input"
                                                value={customer.password || ''}
                                                onChange={handleCustomerChange}
                                                required
                                            />
                                        </div>
                                        <div className="form-item">
                                            <label className="form-label-title">Confirmez le mot de passe</label>
                                            <input
                                                type="password"
                                                name="passwordConfirm"
                                                placeholder="••••••••"
                                                className="custom-input"
                                                value={customer.passwordConfirm || ''}
                                                onChange={handleCustomerChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    {customer.password && customer.passwordConfirm && customer.password !== customer.passwordConfirm && (
                                        <p style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>Les mots de passe ne correspondent pas.</p>
                                    )}

                                    <div className="login-prompt-booking" style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                                        <button
                                            className="text-link-btn"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setAuthMode('login');
                                                setShowAuthModal(true);
                                            }}
                                            style={{ background: 'none', border: 'none', color: '#facc15', cursor: 'pointer', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'underline' }}
                                        >
                                            Déjà client ? Connectez-vous
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="user-logged-in-notice" style={{ marginTop: '1.5rem', padding: '1rem', background: 'rgba(34, 197, 94, 0.05)', borderRadius: '0.75rem', border: '1px solid rgba(34, 197, 94, 0.1)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                    <div style={{ width: '30px', height: '30px', background: '#22c55e', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.8rem' }}>✓</div>
                                    <div>
                                        <p style={{ margin: 0, fontWeight: 600, color: '#f8fafc', fontSize: '0.9rem' }}>Connecté en tant que {customer.firstName}</p>
                                        <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>Vos devis seront enregistrés dans votre espace.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="form-item" style={{ marginTop: '1.5rem' }}>
                            <label className="form-label-title">Détail de la demande (Optionnel)</label>
                            <textarea
                                name="notes"
                                rows="3"
                                placeholder="Précisez le problème, la situation (perte totale, voyant allumé, etc.)"
                                className="custom-input custom-textarea"
                                value={customer.notes || ''}
                                onChange={handleCustomerChange}
                            ></textarea>
                        </div>

                        <div className="step-actions split">
                            <button className="secondary-button" onClick={prevStep}>← Retour</button>
                            <button
                                className="hero-cta"
                                onClick={nextStep}
                                disabled={
                                    !customer.email ||
                                    !customer.phone ||
                                    (!user && (!customer.password || customer.password.length < 8 || customer.password !== customer.passwordConfirm))
                                }
                            >
                                Confirmer le rendez-vous
                            </button>
                        </div>
                    </div>
                );
            case 4:
                const selectedServicesList = dbServices.filter(s => services[s.id]);
                return (
                    <div className="step-content fadeIn">
                        <h3 className="step-title">Finalisez votre demande</h3>
                        <div className="step-4-split-layout">
                            <div className="selection-column">
                                {categories.map(cat => {
                                    const catServices = dbServices.filter(s => s.category === cat.id);
                                    if (catServices.length === 0) return null;
                                    return (
                                        <div key={cat.id} className="category-group">
                                            <h4 className="group-title">{cat.icon} {cat.label}</h4>
                                            <div className="checkbox-group grid-1">
                                                {catServices.map(service => (
                                                    <label key={service.id} className={`service-card-label horizontal ${services[service.id] ? 'selected' : ''}`}>
                                                        <input
                                                            type="checkbox"
                                                            id={service.id}
                                                            checked={!!services[service.id]}
                                                            onChange={handleServiceChange}
                                                            className="service-checkbox"
                                                        />
                                                        <span className="service-name">{service.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="recap-column">
                                <div className="recap-sticky-card">
                                    <div className="recap-header">
                                        <h4>Votre Récapitulatif</h4>
                                    </div>
                                    <div className="recap-body">
                                        <div className="recap-section">
                                            <span className="section-label">Véhicule</span>
                                            <div className="plate-wrapper mini-recap-plate">
                                                <div className="plate-strip left">
                                                    <span className="euro-stars">★</span>
                                                    <span className="country-code">F</span>
                                                </div>
                                                <div className="plate-display-text">{vehicle.plate || 'AA-000-AA'}</div>
                                                <div className="plate-strip right">
                                                    <div className="region-logo" style={{ backgroundImage: 'url(/logo.png)' }}></div>
                                                    <span className="dept-code">31</span>
                                                </div>
                                            </div>
                                            <div className="recap-info-text-large">
                                                {vehicle.brand} {vehicle.model} {vehicle.year}
                                            </div>
                                            <div className="recap-info-text" style={{ textTransform: 'capitalize' }}>
                                                {vehicle.fuel}
                                            </div>
                                        </div>
                                        <div className="recap-section">
                                            <span className="section-label">Prestations ({priceDetails.lines.length})</span>
                                            <div className="recap-items-list">
                                                {priceDetails.lines.map(line => (
                                                    <div key={line.id} className="recap-item">
                                                        <span>{line.label}</span>
                                                        <span className="item-price">{line.price} €</span>
                                                        <button
                                                            className="remove-mini-btn"
                                                            onClick={() => handleServiceChange({ target: { id: line.id, checked: false } })}
                                                        >✕</button>
                                                    </div>
                                                ))}
                                                {priceDetails.activeSurcharges.map((surcharge, idx) => (
                                                    <div key={idx} className="recap-item surcharge-item">
                                                        <div className="item-main">
                                                            <span className="fee-icon">🛡️</span>
                                                            <span>{surcharge.label}</span>
                                                        </div>
                                                        <span className="item-price">
                                                            {surcharge.amount > 0 ? '+' : ''}{Math.round(surcharge.amount)} €
                                                        </span>
                                                    </div>
                                                ))}
                                                <div className="recap-item travel-fee-item">
                                                    <div className="item-main">
                                                        <span className="fee-icon">🚗</span>
                                                        <span>Frais de déplacement</span>
                                                    </div>
                                                    <span className="fee-mention">Selon distance</span>
                                                </div>
                                                {priceDetails.lines.length === 0 && <p className="empty-recap">Aucun service choisi</p>}
                                            </div>
                                        </div>
                                        <div className="recap-section price-total-section">
                                            <span className="section-label">Estimation Totale</span>
                                            <div className="total-price-display">
                                                {priceDetails.total} €
                                            </div>
                                            <button
                                                className="secondary-button small-btn"
                                                style={{ marginTop: '0.5rem', width: 'auto', alignSelf: 'center', fontSize: '0.75rem', gap: '0.5rem' }}
                                                onClick={() => setShowSimulation(true)}
                                            >
                                                👁️ Aperçu de mon devis
                                            </button>
                                            <div className="price-factors">
                                                {priceDetails.activeSurcharges.map((s, i) => (
                                                    <span key={i} className="factor-tag highlight">{s.label}</span>
                                                ))}
                                                {priceDetails.activeSurcharges.length === 0 && (
                                                    <span className="factor-tag">Tarif Base</span>
                                                )}
                                            </div>
                                            <span className="price-mention">* Prix indicatif sous réserve de diagnostic final</span>
                                        </div>
                                        <div className="recap-section">
                                            <span className="section-label">Contact</span>
                                            <div className="recap-info-text">{customer.firstName} {customer.lastName}</div>
                                            <div className="recap-info-text">{customer.phone}</div>
                                        </div>
                                    </div>
                                    <div className="recap-footer unified-actions">
                                        <div className="main-actions-grid">
                                            <button
                                                className="secondary-button quote-action-btn"
                                                onClick={(e) => {
                                                    if (onSubmitQuote) {
                                                        onSubmitQuote(e, 'quote');
                                                    }
                                                }}
                                                disabled={selectedServicesList.length === 0}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
                                                Recevoir mon Devis
                                            </button>

                                            <button
                                                className="hero-cta flash-btn booking-action-btn"
                                                onClick={() => setCurrentStep(5)}
                                                disabled={selectedServicesList.length === 0}
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                                                Prendre Rendez-vous
                                            </button>
                                        </div>
                                        <button className="text-button small-text back-step-btn" onClick={prevStep} style={{ marginTop: '1rem', width: '100%' }}>
                                            ← Modifier mes informations
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            case 5:
                return (
                    <div className="step-content fadeIn">
                        <h3 className="step-title">Planifiez votre intervention</h3>
                        <div className="calendar-selection-container">
                            <span className="form-label-title center">Choisissez une date</span>
                            <div className="dates-scroll-container">
                                {availableDates.map((d, i) => {
                                    const dateStr = d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
                                    return (
                                        <div
                                            key={i}
                                            className={`date-card-item ${selectedDate === dateStr ? 'selected' : ''}`}
                                            onClick={() => handleDateSelect(d)}
                                        >
                                            <span className="date-day-name">{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</span>
                                            <span className="date-day-num">{d.getDate()}</span>
                                            <span className="date-month-name">{d.toLocaleDateString('fr-FR', { month: 'short' })}</span>
                                        </div>
                                    );
                                })}
                            </div>

                            {selectedDate && (
                                <div className="time-selection-section animation-fadeIn">
                                    <span className="form-label-title center" style={{ marginTop: '2rem' }}>Choisissez un horaire pour le {selectedDate}</span>
                                    {bookingLoading ? (
                                        <div className="loading-slots">Recherche de disponibilités...</div>
                                    ) : (
                                        <div className="time-slots-grid">
                                            {availableSlots.map(slot => (
                                                <button
                                                    key={slot}
                                                    className={`time-slot-btn ${selectedTime === slot ? 'selected' : ''}`}
                                                    onClick={() => setSelectedTime(slot)}
                                                >
                                                    {slot}
                                                </button>
                                            ))}
                                            {availableSlots.length === 0 && <p className="no-slots">Plus de disponibilités pour cette date.</p>}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="step-actions split" style={{ marginTop: '3rem' }}>
                            <button className="secondary-button" onClick={() => setCurrentStep(4)}>← Retour au récapitulatif</button>
                            <button
                                className="hero-cta flash-btn"
                                disabled={!selectedDate || !selectedTime}
                                onClick={(e) => {
                                    if (onSubmitQuote) {
                                        // On passe les infos de date et heure en data additionnelle
                                        onSubmitQuote(e, 'booking', { date: selectedDate, time: selectedTime });
                                    }
                                }}
                            >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                Confirmer ma réservation
                            </button>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div id="booking-config" className="booking-configurator-wrapper">
            {agencySettings?.closure_banner_active && (
                <div className="closure-alert-banner fadeIn">
                    <div className="alert-icon">📅</div>
                    <div className="alert-content">
                        <h4>Avis d'indisponibilité</h4>
                        <p>{agencySettings.closure_auto_responder || "L'atelier est actuellement fermé pour congés ou déplacement. Les réservations sont temporairement indisponibles."}</p>
                    </div>
                </div>
            )}
            <section className="config-card">
                <div className="config-progress-bar">
                    {[1, 2, 3, 4, 5].map(step => {
                        const isAccessible = step <= currentStep || (
                            step === 2 ? (vehicle.plate && hasAnyService) :
                                step === 3 ? (vehicle.plate && hasAnyService && vehicle.brand && vehicle.model && vehicle.year) :
                                    step === 4 ? (vehicle.plate && hasAnyService && vehicle.brand && vehicle.model && vehicle.year && customer.email && customer.phone) :
                                        step === 5 ? (currentStep === 5) : false
                        );
                        return (
                            <div
                                key={step}
                                className={`progress-step-item ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''} ${isAccessible ? 'clickable' : 'disabled'}`}
                                onClick={() => isAccessible && setCurrentStep(step)}
                            >
                                <div className="step-circle">{step}</div>
                                <span className="step-name-text">
                                    {step === 1 ? 'Besoin' : step === 2 ? 'Véhicule' : step === 3 ? 'Contact' : step === 4 ? 'Détails' : 'Agenda'}
                                </span>
                            </div>
                        );
                    })}
                    <div className="progress-line-track">
                        <div className="progress-line-fill" style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}></div>
                    </div>
                </div>

                <div className="plate-persistent-section">
                    <div className="plate-section-home">
                        <span className="form-label-title center">Votre Immatriculation</span>
                        <div className={`plate-wrapper ${showTour && tourStep === 0 ? 'tour-highlight' : ''}`} id="tour-step-plate">
                            <div className="plate-strip left">
                                <span className="euro-stars">★</span>
                                <span className="country-code">F</span>
                            </div>
                            <input
                                type="text"
                                name="plate"
                                value={vehicle.plate}
                                onChange={handleVehicleChange}
                                placeholder="AB-123-CD"
                                className="plate-input"
                            />
                            <div className="plate-strip right">
                                <div className="region-logo" style={{ backgroundImage: 'url(/logo.png)' }}></div>
                                <span className="dept-code">31</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="config-body-container">
                    {renderStepContent()}
                </div>
            </section>

            <HomeBooking
                user={user}
                onLoginClick={() => {
                    setAuthMode('login');
                    setShowAuthModal(true);
                }}
                agencySettings={agencySettings}
            />

            {showSimulation && (
                <div className="quote-simulation-overlay" onClick={() => setShowSimulation(false)}>
                    <div className="quote-document-container fadeIn" onClick={e => e.stopPropagation()}>
                        <button className="close-sim-btn" onClick={() => setShowSimulation(false)}>×</button>
                        <div className="quote-watermark">PROVISOIRE</div>
                        {/* Simulation content omitted for brevity but same as Home.js */}
                        <div className="quote-header">
                            <div className="quote-logo-section">
                                <h2>L'UNIVERS DIAG</h2>
                                <p style={{ fontSize: '0.8rem', opacity: 0.8 }}>Expertise & Diagnostic Automobile</p>
                            </div>
                            <div className="quote-meta-info">
                                <p><strong>Simulation N° :</strong> SIM-{new Date().getFullYear()}-{Math.floor(1000 + Math.random() * 9000)}</p>
                                <p><strong>Date :</strong> {new Date().toLocaleDateString('fr-FR')}</p>
                            </div>
                        </div>
                        <div className="quote-subject-grid">
                            <div className="subject-block">
                                <h4>Client</h4>
                                <div className="subject-details">
                                    <p><strong>Nom :</strong> {customer.firstName} {customer.lastName}</p>
                                    <p><strong>Email :</strong> {customer.email}</p>
                                </div>
                            </div>
                            <div className="subject-block">
                                <h4>Véhicule</h4>
                                <div className="subject-details">
                                    <p><strong>Type :</strong> {vehicle.vehicle_type}</p>
                                    <p><strong>Marque :</strong> {vehicle.brand} {vehicle.model}</p>
                                    <p><strong>Immat. :</strong> {vehicle.plate || 'Non renseignée'}</p>
                                </div>
                            </div>
                        </div>
                        <div className="quote-table-container">
                            <table className="quote-table">
                                <thead>
                                    <tr>
                                        <th>Description</th>
                                        <th className="col-price">Total Est.</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {priceDetails.lines.map((line, i) => (
                                        <tr key={i}>
                                            <td>{line.label}</td>
                                            <td className="col-price">{line.price} €</td>
                                        </tr>
                                    ))}
                                    {priceDetails.activeSurcharges.map((s, i) => (
                                        <tr key={`s-${i}`} style={{ fontStyle: 'italic', opacity: 0.8 }}>
                                            <td>Ajustement : {s.label}</td>
                                            <td className="col-price">+{Math.round(s.amount)} €</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="quote-total-block">
                            <div className="total-row grand-total">
                                <span>TOTAL TTC</span>
                                <span>{priceDetails.total} €</span>
                            </div>
                        </div>
                        <div className="quote-footer-pro">
                            <p>L'Univers Diag - Spécialiste électronique automobile</p>
                            <p style={{ marginTop: '0.5rem', opacity: 0.7 }}>* Estimation basée sur vos informations.</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingConfigurator;
